/**
 * Smart Records demo step — premium "wow" preview of the vertical client record
 * Used in all 4 vertical demos as STEP 2 to position SmartBooker as a Business OS,
 * not a booking SaaS. Pure UI mockup (no real data fetched).
 *
 * Per-sector mock data via SECTOR_RECORDS config.
 */
import React, { useEffect, useState } from "react";
import {
  Sparkles, AlertTriangle, Lightbulb, TrendingUp, Calendar, FileText,
  Stethoscope, Activity, Pill, Scissors, Wand2, Palette, Heart, ClipboardList,
  Crown, Shield, Phone, MessageSquare, Mail, Clock, Star, Users, ChevronRight,
} from "lucide-react";

// Sector-specific realistic mock data — each one tells the story of a credible client
const SECTOR_RECORDS = {
  odonto: {
    icon: Stethoscope,
    grad: "from-cyan-500 to-blue-600",
    soft: "from-cyan-50 to-blue-50",
    badge: "Studio Dentistico",
    client: { name: "Alessandra Bruno", subtitle: "Paziente · da 3 anni", spent: "€570", visits: 6, last: "17 apr", next: "27 mag" },
    headline: "Anamnesi · Cure · Preventivi · Piano",
    fields: [
      { label: "Allergie", value: "Penicillina" },
      { label: "Patologie", value: "Diabete tipo 2" },
      { label: "Fumatrice", value: "Sì" },
    ],
    rows: [
      { date: "22 apr", main: "Otturazione · 4.6", sub: "Dr. Sorriso · completata" },
      { date: "10 apr", main: "Igiene professionale", sub: "Dr.ssa Bianchi · completata" },
      { date: "—",     main: "Preventivo €1.420", sub: "Implantologia · in attesa" },
    ],
    ai: {
      summary: "Paziente con diabete e allergia penicillina. Ultima igiene oltre 30 giorni fa. Preventivo €1.420 in attesa di accettazione da 12 giorni.",
      alerts: ["Allergia penicillina segnalata", "Igiene da rinnovare (>30gg)"],
      suggestion: "Recall WhatsApp per follow-up preventivo + screening parodontale",
    },
  },
  fisio: {
    icon: Activity,
    grad: "from-emerald-500 to-teal-600",
    soft: "from-emerald-50 to-teal-50",
    badge: "Studio Fisio · Osteo",
    client: { name: "Marco Verdi", subtitle: "Paziente · 5° seduta su 10", spent: "€275", visits: 5, last: "ieri", next: "lunedì" },
    headline: "Problematiche · Sedute · Esercizi · Evoluzione",
    fields: [
      { label: "Zona dolore", value: "Lombare" },
      { label: "VAS iniziale", value: "8/10" },
      { label: "VAS attuale", value: "3/10 ↓" },
    ],
    rows: [
      { date: "Sed. 5", main: "Tecar + manipolazione L4-L5", sub: "VAS 5→3 · migliorato" },
      { date: "Sed. 4", main: "Esercizi McKenzie + core", sub: "VAS 6→5 · stabile" },
      { date: "Casa",   main: "Plank 3×30s, Bird-dog 3×8", sub: "Frequenza 4×/sett." },
    ],
    ai: {
      summary: "Paziente in netto miglioramento: VAS 8→3 in 5 sedute. Buona aderenza esercizi domiciliari. Stimate altre 3-4 sedute per dimissione.",
      alerts: ["VAS in calo costante: continuare protocollo", "Postura lavorativa da rivedere"],
      suggestion: "Pianificare seduta di rieducazione ergonomica + dimissione tra 3-4 sedute",
    },
  },
  estetica: {
    icon: Sparkles,
    grad: "from-pink-500 to-rose-600",
    soft: "from-pink-50 to-rose-50",
    badge: "Centro Estetico",
    client: { name: "Sara Conti", subtitle: "Cliente · pacchetto premium", spent: "€890", visits: 14, last: "3 gg fa", next: "venerdì" },
    headline: "Profilo pelle · Trattamenti · Foto · Pacchetti",
    fields: [
      { label: "Tipologia pelle", value: "Mista" },
      { label: "Fototipo", value: "III · Fitzpatrick" },
      { label: "Allergie", value: "Acido glicolico" },
    ],
    rows: [
      { date: "8 mag",  main: "Radiofrequenza viso", sub: "Cabina 2 · esito ottimo" },
      { date: "29 apr", main: "Pulizia + peeling enzimatico", sub: "Estetista Senior" },
      { date: "Pacc.",  main: "Anti-age 6 sedute · 4/6 usate", sub: "Scadenza 30 set · €420" },
    ],
    ai: {
      summary: "Cliente fidelizzata con pacchetto anti-age in corso (4/6 sedute). Pelle mista responsiva ai trattamenti recenti. Buona finestra di cross-sell.",
      alerts: ["Allergia acido glicolico segnalata", "Pacchetto in esaurimento (2 sedute)"],
      suggestion: "Proporre rinnovo pacchetto + trattamento corpo complementare",
    },
  },
  hair: {
    icon: Scissors,
    grad: "from-amber-500 to-orange-600",
    soft: "from-amber-50 to-orange-50",
    badge: "Salone Hair",
    client: { name: "Giulia Rossi", subtitle: "Cliente · ogni 5 settimane", spent: "€1.240", visits: 18, last: "12 gg fa", next: "—" },
    headline: "Capello · Servizi · Colorazioni · Fidelizzazione",
    fields: [
      { label: "Lunghezza", value: "Medi · mossi" },
      { label: "Texture · % canuti", value: "Fine · 25%" },
      { label: "Colore attuale", value: "Biondo miele" },
    ],
    rows: [
      { date: "26 apr", main: "Colore + taglio + piega", sub: "Stylist Senior · €75" },
      { date: "Form.",  main: "L'Oréal 8.31 + 9.0", sub: "Ossigeno 6% · 35 min posa" },
      { date: "Casa",   main: "Kérastase Chronologiste", sub: "Maschera 1×/sett." },
    ],
    ai: {
      summary: "Cliente premium con frequenza 5 settimane stabile. Formula colore archiviata replicabile. Ultima visita 12gg fa: in linea con il pattern.",
      alerts: ["Prossima visita consigliata: settimana prossima", "Rischio churn basso (1/5)"],
      suggestion: "Inviare WhatsApp per prenotare prossimo colore + suggerire trattamento ricostruttivo",
    },
  },
};

const SmartRecordsStep = ({ sector = "odonto", onCta, t }) => {
  const cfg = SECTOR_RECORDS[sector] || SECTOR_RECORDS.odonto;
  const SectorIcon = cfg.icon;
  const [reveal, setReveal] = useState(0);

  useEffect(() => {
    const timers = [];
    [220, 440, 660, 900, 1180].forEach((ms, i) => {
      timers.push(setTimeout(() => setReveal(i + 1), ms));
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  const tt = t || ((it) => it);

  return (
    <div className="py-6 sm:py-10" data-testid={`smart-records-step-${sector}`}>
      {/* HEADLINE */}
      <div className="text-center mb-8 sm:mb-10 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-50 to-cyan-50 border border-violet-200/50 text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700 mb-4 shadow-sm">
          <Crown className="w-3 h-3" /> {tt("Smart Records · Premium", "Smart Records · Premium")}
        </div>
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
          {tt("Ogni cliente, un fascicolo intelligente.", "Every client, a smart record.")}
        </h2>
        <p className="text-slate-500 mt-3 text-base">
          {tt(
            `Non solo un'agenda: il sistema operativo per il tuo ${cfg.badge.toLowerCase()}. Anamnesi, storico, documenti, AI insight — tutto in un'unica scheda.`,
            `Not just a calendar: the operating system for your ${cfg.badge.toLowerCase()}. History, documents, AI insights — all in one place.`
          )}
        </p>
      </div>

      {/* MOCKUP — 3 col Stripe/Attio style */}
      <div className="rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-50/60 to-white p-3 sm:p-5 shadow-2xl shadow-slate-200/50">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-2 mb-3">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
          <span className="ml-3 text-[10px] text-slate-400 font-mono">smartbooker.it/dashboard/client/...</span>
        </div>

        {/* Hero header */}
        <div className={`rounded-2xl border border-slate-200/60 bg-gradient-to-br ${cfg.soft} p-4 sm:p-5 transition-all duration-700 ${reveal >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="flex items-start gap-3 sm:gap-4 flex-wrap">
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${cfg.grad} text-white flex items-center justify-center text-lg sm:text-xl font-black shadow-lg flex-shrink-0`}>
              {cfg.client.name.split(" ").map(p => p[0]).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-black text-slate-900 truncate">{cfg.client.name}</h3>
              <p className="text-xs text-slate-500">{cfg.client.subtitle}</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/80 text-slate-700 inline-flex items-center gap-1`}>
                  <SectorIcon className="w-3 h-3" /> {cfg.badge}
                </span>
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                  <Shield className="w-2.5 h-2.5" /> GDPR
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 ml-auto">
              <Stat label="Speso" value={cfg.client.spent} />
              <Stat label="Visite" value={cfg.client.visits} />
              <Stat label="Ultima" value={cfg.client.last} />
              <Stat label="Prossima" value={cfg.client.next} />
            </div>
          </div>
        </div>

        {/* 3-col body */}
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* LEFT — sidebar */}
          <aside className={`lg:col-span-3 rounded-2xl border border-slate-200/60 bg-white p-3.5 transition-all duration-700 delay-150 ${reveal >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2.5">Anagrafica</h4>
            <div className="space-y-1.5 text-xs">
              {cfg.fields.map(f => (
                <div key={f.label} className="flex justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
                  <span className="text-slate-500">{f.label}</span>
                  <span className="font-medium text-slate-800 text-right truncate max-w-[60%]">{f.value}</span>
                </div>
              ))}
            </div>
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-4 mb-2">Consensi</h4>
            <div className="space-y-1">
              {[["Privacy", true], ["Marketing", true], ["WhatsApp", true]].map(([l, on]) => (
                <div key={l} className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-600">{l}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {on ? "ON" : "OFF"}
                  </span>
                </div>
              ))}
            </div>
          </aside>

          {/* CENTER */}
          <main className={`lg:col-span-6 rounded-2xl border border-slate-200/60 bg-white p-3.5 transition-all duration-700 delay-300 ${reveal >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <ClipboardList className={`w-4 h-4 text-transparent bg-gradient-to-br ${cfg.grad} bg-clip-text`} />
                {cfg.headline}
              </h4>
              <span className="text-[9px] font-bold text-slate-400">v1</span>
            </div>
            <ul className="space-y-2">
              {cfg.rows.map((r, i) => (
                <li key={`${r.main}-${r.date}-${i}`} className={`flex items-center gap-3 p-2.5 rounded-xl border border-slate-100 hover:border-violet-300 transition-colors ${reveal >= 3 ? "" : "opacity-0"}`}>
                  <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${cfg.grad} text-white flex items-center justify-center flex-shrink-0`}>
                    <SectorIcon className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{r.main}</p>
                    <p className="text-[10px] text-slate-500 truncate">{r.sub}</p>
                  </div>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{r.date}</span>
                </li>
              ))}
            </ul>

            {/* Mini timeline preview */}
            <div className={`mt-4 pt-3 border-t border-slate-100 transition-all duration-700 delay-500 ${reveal >= 4 ? "opacity-100" : "opacity-0"}`}>
              <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Timeline</h5>
              <ol className="relative border-l-2 border-violet-100 ml-1.5 space-y-2.5">
                {[
                  { icon: FileText, text: "Documento caricato · TAC.pdf", time: "1h fa" },
                  { icon: MessageSquare, text: "WhatsApp inviato · conferma appuntamento", time: "ieri" },
                  { icon: Sparkles, text: "AI Summary aggiornato", time: "ieri" },
                ].map((e) => (
                  <li key={e.text} className="ml-3 relative">
                    <span className="absolute -left-[18px] top-0.5 w-4 h-4 rounded-full bg-violet-100 border-2 border-white flex items-center justify-center">
                      <e.icon className="w-2 h-2 text-violet-600" />
                    </span>
                    <div className="flex justify-between gap-2 text-[11px]">
                      <span className="text-slate-700 truncate">{e.text}</span>
                      <span className="text-slate-400 whitespace-nowrap">{e.time}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </main>

          {/* RIGHT — AI panel */}
          <aside className={`lg:col-span-3 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-cyan-50 p-3.5 transition-all duration-700 delay-500 ${reveal >= 4 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <div className="flex items-center gap-2 mb-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-900 leading-tight">AI Assistant</p>
                <p className="text-[9px] text-slate-500">Claude Sonnet 4.5</p>
              </div>
            </div>
            <div className="rounded-lg bg-white/80 border border-violet-100 p-2 mb-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-violet-700 mb-1">Riassunto</p>
              <p className="text-[11px] text-slate-700 leading-relaxed">{cfg.ai.summary}</p>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 mb-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" /> Alert
              </p>
              <ul className="space-y-0.5">
                {cfg.ai.alerts.map((a) => (
                  <li key={a} className="text-[10px] text-slate-700">• {a}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 mb-1 flex items-center gap-1">
                <Lightbulb className="w-2.5 h-2.5" /> Suggerimento
              </p>
              <p className="text-[10px] text-slate-700">→ {cfg.ai.suggestion}</p>
            </div>
          </aside>
        </div>
      </div>

      {/* Why it matters */}
      <div className={`mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 transition-all duration-700 delay-700 ${reveal >= 5 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
        {[
          { icon: Crown,    title: "Enterprise look",    desc: "UX premium che alza la percezione del tuo brand" },
          { icon: Shield,   title: "GDPR-ready",         desc: "Audit log + consensi tracciati per ogni cliente" },
          { icon: Sparkles, title: "AI sul tuo settore", desc: "Suggerimenti personalizzati per dental / fisio / estetica / hair" },
        ].map((card) => (
          <div key={card.title} className="rounded-2xl border border-slate-200 bg-white p-3.5 hover:border-violet-400 transition-colors">
            <card.icon className="w-5 h-5 text-violet-500 mb-2" />
            <p className="text-sm font-bold text-slate-900">{card.title}</p>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-xl bg-white/70 backdrop-blur border border-slate-200/60 px-2 py-1.5 min-w-[80px]">
    <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
    <p className="text-[13px] font-black text-slate-900 leading-tight">{value}</p>
  </div>
);

export default SmartRecordsStep;
